import { Calendar } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type FocusEvent, type InputHTMLAttributes, type MouseEvent, type ReactNode, type Ref, type TextareaHTMLAttributes } from "react";
import { DATUM_TEXTE, datumAnzeige, datumLesen, heuteIso } from "./datum";
import { Monatsblatt } from "./Monatsblatt";

/* Feld (Handoff Schiefer): H 48, Radius 10, weiss mit Umrandung --pg-border-strong statt grauer
   Fuellung, Beschriftung 12/600 darueber, Fokus 1,5 px Akzent + 3 px Toenung, Fehler (aria-invalid)
   1,5 px Gefahrrot. Rand und Ring sind Schatten (--pg-feld-*), kein border: so trifft der Fokusring nie
   auf einen Rahmen (Abnahme-Regel 1). Innenabstand 12 statt 14 (4-px-Raster, Regel 9). */
const FELD =
  "t-body w-full rounded-md bg-pg-surface px-3 text-pg-text shadow-(--pg-feld-rand) outline-none transition-shadow duration-(--pg-dur-fast) placeholder:text-pg-text-3 focus-visible:shadow-(--pg-feld-fokus) aria-invalid:shadow-(--pg-feld-fehler) disabled:opacity-50";

/** W52b (W38-3 R1 H1): der Hinweis unter dem Feld ist seine Beschreibung, ein Screenreader liest ihn mit dem Feld vor.
 *  Ein schon uebergebenes `aria-describedby` bleibt vorne; ohne Hinweis kein zusaetzlicher Verweis. */
function beschreibung(eigen: string | undefined, hinweisId: string | undefined): string | undefined {
  return [eigen, hinweisId].filter(Boolean).join(" ") || undefined;
}

type FeldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; hinweis?: ReactNode; ref?: Ref<HTMLInputElement> };

/** Druck ohne Fokuswechsel (Kalender-Knopf): der Fokus bleibt im Tippfeld, bis der Klick ihn ins Monatsblatt legt. Sonst gäbe
 *  Safari ihn beim Tipp an body ab und die Fokuswache des Blatts (Sheet.tsx, H-R3-2) legte ihn auf „Abbrechen“. */
const haltenBeimDruck = (e: MouseEvent) => e.preventDefault();

/** W82 Web-5h-N2b (b1, Frage 61 = B, D-661, D-663; HW21, HW22, HW29): Datumsfeld als Tippfeld TT.MM.JJJJ mit Ziffernblock
 *  (DW01) und dem Kalender-Knopf rechts im Feld (DW02); der Knopf öffnet darunter, im Blatt, das Monatsblatt. Angenommen werden
 *  TT.MM.JJJJ, T.M.JJJJ, TTMMJJJJ und JJJJ-MM-TT (Einfügen), Punkte setzt das Feld beim Verlassen; ein Tag, den es nicht gibt,
 *  ist kein Datum. Nach außen wie bisher: `value` und `onChange` mit JJJJ-MM-TT in `e.target.value`; gemeldet wird nur ein
 *  vollständiger gültiger Tag oder leer, ein fremdes neues `value` ersetzt den Text. Ungültig beim Verlassen: `aria-invalid` und
 *  DW09 oder DW10, nie zugleich mit dem Fehlersatz des Aufrufers (der hat Vorrang). Passen die sieben Tage zu 44 px nicht in
 *  die Zeile (320 px, Frage 80 = A), fehlt der Knopf und das Feld ist nur Tippfeld. Escape schließt nur das Monatsblatt (das
 *  Blatt lässt es über `data-esc-eigen` in Ruhe) und gibt den Fokus an den Knopf zurück; nach der Wahl liegt er dort (K38). */
function Datumsfeld({ id, className, beschrieben, props, unten, zeile = false }: { id: string; className: string; beschrieben: string | undefined; props: Omit<FeldProps, "label" | "hinweis" | "className" | "aria-describedby">; unten?: ReactNode; zeile?: boolean }) {
  // `type` bleibt draußen: das Tippfeld ist ein Textfeld
  const { ref, onChange, onBlur, onKeyDown, value, defaultValue, min, max, type, "aria-invalid": fremderFehler, ...rest } = props;
  void type;
  const blattId = useId();
  const fehlerId = useId();
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const blatt = useRef<HTMLDivElement>(null);
  const [eigen, setEigen] = useState(String(defaultValue ?? ""));
  const wert = value !== undefined ? String(value ?? "") : eigen;
  const [text, setText] = useState(() => datumAnzeige(wert));
  // der zuletzt gemeldete oder von außen gekommene Wert: weicht `value` davon ab, kam er von außen und ersetzt den Text
  const [bekannt, setBekannt] = useState(wert);
  const [fehler, setFehler] = useState<"form" | "fehlt" | null>(null);
  const [offen, setOffen] = useState(false);
  const [passt, setPasst] = useState(true);
  const oeffnen = useRef(false);
  // nach einer Wahl im Monatsblatt: das Feld gilt als verlassen (`onBlur` des Aufrufers mit dem neuen Wert, etwa das Sichern in
  // „Brief bearbeiten“), sobald der Wert beim Aufrufer angekommen ist
  const [gewaehlt, setGewaehlt] = useState(0);
  const verlassen = useRef(onBlur);
  useLayoutEffect(() => {
    verlassen.current = onBlur;
  });
  if (wert !== bekannt) {
    setBekannt(wert);
    setText(datumAnzeige(wert));
    setFehler(null);
  }
  const blattOffen = offen && passt;
  const min0 = min === undefined ? undefined : String(min);
  const max0 = max === undefined ? undefined : String(max);

  // passt das Raster (sieben Tage zu --touch) in die Zeile? Gemessen an der Zeile samt Innenrand, in den das Monatsblatt reicht
  useLayoutEffect(() => {
    const h = huelle.current;
    if (!h || typeof ResizeObserver === "undefined") return;
    const ort = h.closest("li") ?? h.parentElement ?? h;
    // ohne Layout (0 px, etwa noch nicht im Bild) gilt es als passend; gemessen wird wieder, sobald die Zeile eine Breite hat
    const messen = () => setPasst(ort.clientWidth === 0 || ort.clientWidth >= 7 * (parseFloat(getComputedStyle(h).getPropertyValue("--touch")) || 44));
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(ort);
    return () => beobachter.disconnect();
  }, []);

  // geöffnet per Knopf: der Fokus geht auf den gewählten (sonst heutigen) Tag im Raster (APG)
  useLayoutEffect(() => {
    if (!blattOffen || !oeffnen.current) return;
    oeffnen.current = false;
    blatt.current?.querySelector<HTMLButtonElement>('[data-tag][tabindex="0"]')?.focus({ preventScroll: true });
    // W82 Merge-6 (M1): das Monatsblatt liegt nach dem Öffnen ganz im Bild (nur so weit rollen, wie nötig; auf der Seite über der
    // Tab-Leiste, im Blatt in dessen Ausschnitt); ohne das stünde es bei einem Feld in der unteren Hälfte unter dem Rand
    blatt.current?.closest(".datum-monatsblatt")?.scrollIntoView?.({ block: "nearest", behavior: "instant" });
  }, [blattOffen]);

  const melden = (iso: string) => {
    setBekannt(iso);
    if (value === undefined) setEigen(iso);
    // die Aufrufer lesen `e.target.value` (JJJJ-MM-TT) wie beim nativen Feld
    const ziel = { value: iso, name: rest.name ?? "" };
    onChange?.({ target: ziel, currentTarget: ziel, type: "change" } as unknown as ChangeEvent<HTMLInputElement>);
  };

  const schliessen = () => {
    knopf.current?.focus({ preventScroll: true });
    setOffen(false);
  };

  const waehlen = (iso: string) => {
    schliessen();
    setText(datumAnzeige(iso));
    setFehler(null);
    if (iso !== bekannt) {
      melden(iso);
      setGewaehlt((n) => n + 1);
    }
  };

  useEffect(() => {
    const f = huelle.current?.querySelector("input");
    if (!gewaehlt || !f) return;
    verlassen.current?.({ target: f, currentTarget: f, type: "blur", relatedTarget: knopf.current } as unknown as FocusEvent<HTMLInputElement>);
  }, [gewaehlt]);

  // HW21: Öffnen und Schließen ändern die Höhe des Blatts ohne Ereignis des Ausschnitts; die Haltung des Blatts (Sheet.tsx) misst
  // ihre Lage nur dort nach. Ein `resize` am Ausschnitt nach dem Wechsel lässt sie die Oberkante neu halten, statt dass das Blatt
  // um die Höhe des Monatsblatts springt (gilt nur im mobilen Blatt, `data-sicht`)
  const erstesMal = useRef(true);
  useLayoutEffect(() => {
    if (erstesMal.current) {
      erstesMal.current = false;
      return;
    }
    const vv = window.visualViewport;
    if (!vv || !huelle.current?.closest("[data-sicht]")) return;
    const takt = requestAnimationFrame(() => vv.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(takt);
  }, [blattOffen]);

  // W82 Web-6a (WN-06, D-860 A): Escape schließt zuerst das offene Monatsblatt, egal wo der Fokus steht (Raster, Feld, daneben im
  // Blatt oder auf der Seite); das Blatt dahinter bleibt offen, denn Radix schließt nicht, wenn das Ereignis schon `defaultPrevented`
  // ist (das Fenster hört vor dem Dokument). Liegt ein anderes Blatt, eine Rückfrage oder ein Menü darüber, gehört der Escape
  // diesem. Den Fokus holt der Kalender-Knopf nur, wenn er im Feld oder im Monatsblatt lag
  useEffect(() => {
    if (!blattOffen) return;
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const h = huelle.current;
      const ebenen = document.querySelectorAll('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"]');
      if ([...ebenen].some((d) => !d.contains(h))) return;
      e.preventDefault();
      const f = document.activeElement;
      if (h?.contains(f) || blatt.current?.contains(f)) knopf.current?.focus({ preventScroll: true });
      setOffen(false);
    };
    window.addEventListener("keydown", escape, true);
    return () => window.removeEventListener("keydown", escape, true);
  }, [blattOffen]);

  const eigenerFehler = fehler !== null && !fremderFehler;
  const lesen = [beschrieben, eigenerFehler ? fehlerId : undefined].filter(Boolean).join(" ") || undefined;

  const monatsblatt = blattOffen && (
    // in der Zeile mit mehreren Feldern (DatumFeld) in voller Zeilenbreite darunter, nie in der schmalen Spalte
    <div className={`datum-monatsblatt ${zeile ? "datum-monatsblatt-zeile" : ""}`}>
      <Monatsblatt ref={blatt} id={blattId} wert={wert} heute={heuteIso()} min={min0} max={max0} onWahl={waehlen} />
    </div>
  );

  return (
    <>
      <div ref={huelle} className={`datum-feld relative ${zeile ? "zeile-feld datum-zeilenfeld flex-1 basis-32" : ""}`} data-esc-eigen={blattOffen ? "" : undefined}>
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          size={12}
          placeholder={DATUM_TEXTE.platzhalter}
          value={text}
          // Grenzen bleiben als Merkmal am Feld (wie beim nativen Feld); gesperrt wird im Monatsblatt, den Satz sagt der Aufrufer
          min={min}
          max={max}
          className={`datum-tippfeld ${FELD} min-h-(--feld-h) ${passt ? "pr-(--touch)" : ""} ${className}`}
          aria-describedby={lesen}
          aria-invalid={fremderFehler || (fehler ? true : undefined)}
          onChange={(e) => {
            const t = e.target.value;
            setText(t);
            setFehler(null);
            const g = datumLesen(t);
            const neu = g.art === "tag" ? g.iso : "";
            if (neu !== bekannt) melden(neu);
          }}
          onKeyDown={onKeyDown}
          onBlur={(e) => {
            const g = datumLesen(text);
            if (g.art === "tag") setText(datumAnzeige(g.iso));
            setFehler(g.art === "form" || g.art === "fehlt" ? g.art : null);
            onBlur?.(e);
          }}
          {...rest}
        />
        {passt && (
          <button
            ref={knopf}
            type="button"
            aria-label={DATUM_TEXTE.kalender}
            aria-expanded={blattOffen}
            aria-controls={blattOffen ? blattId : undefined}
            disabled={rest.disabled}
            data-feld-halt=""
            onMouseDown={haltenBeimDruck}
            onClick={() => {
              if (blattOffen) return schliessen();
              oeffnen.current = true;
              setOffen(true);
            }}
            className="datum-kalender absolute inset-y-0 right-0 grid w-(--touch) place-items-center rounded-md text-pg-text-2 hover:text-pg-text disabled:opacity-50"
          >
            {/* W82 Web-5h2 (V1): das Symbol wächst mit der Schrift (bei 100 % weiter 18 px) */}
            <Calendar size="1.2em" strokeWidth={1.75} aria-hidden />
          </button>
        )}
      </div>
      {eigenerFehler && (
        <span id={fehlerId} className={`datum-fehler t-footnote text-pg-danger ${zeile ? "w-full" : ""}`}>
          {fehler === "form" ? DATUM_TEXTE.form : DATUM_TEXTE.gibtEsNicht}
        </span>
      )}
      {unten}
      {monatsblatt}
    </>
  );
}

/** Eingabezeile in einer Gruppe: Beschriftung ueber dem umrandeten Feld (Formulare). W82 Web-5h-N2b (b3, Stack-Probe F-3,
 *  Alternative A): 8 px zwischen Beschriftung und Feld, oben 4 statt 8 px, so bleibt die Zeile gleich hoch und der Fokusring (3 px
 *  Rahmen mit 3 px Abstand) berührt die Beschriftung nicht; ebenso im Textfeld und in den Auswahlzeilen der Seiten. */
export function Feld(props: FeldProps) {
  const { label, hinweis, className = "", "aria-describedby": eigen, ...rest } = props;
  const id = useId();
  const hinweisId = useId();
  return (
    <li className="zeile zeile-feld relative flex flex-col justify-center gap-2 px-4 pt-1 pb-2">
      <label htmlFor={id} className="t-caption-1 t-caption-1-medium text-pg-text-2">
        {label}
      </label>
      {rest.type === "date" ? (
        <Datumsfeld
          id={id}
          className={className}
          beschrieben={beschreibung(eigen, hinweis ? hinweisId : undefined)}
          props={rest}
          unten={
            hinweis && (
              <span id={hinweisId} className="t-footnote text-pg-text-2">
                {hinweis}
              </span>
            )
          }
        />
      ) : (
        <>
          <input id={id} className={`${FELD} min-h-(--feld-h) ${className}`} aria-describedby={beschreibung(eigen, hinweis ? hinweisId : undefined)} {...rest} />
          {hinweis && (
            <span id={hinweisId} className="t-footnote text-pg-text-2">
              {hinweis}
            </span>
          )}
        </>
      )}
    </li>
  );
}

/** R2 W82-Web-5c (K37, Abweichung 2 des Arbeiters): Datumsfeld ohne eigene Beschriftung fuer Zeilen mit mehreren Feldern
 *  (Pruefen-Blatt, „Eigene Fristen“: Art, „Frist bis“, Entfernen). Gleiche Gestalt und gleiches Verhalten wie `Feld` mit
 *  type="date"; der Name kommt per aria-label. `zeile-feld` traegt die W82-Gestalt (welt.css), ohne die Trennlinie einer Zeile
 *  (`.zeile.zeile-feld`). Die Basis (8 rem, waechst mit der Schrift) stellt es bei grosser Schrift in eine eigene Zeile. W82
 *  Web-5h2 (K46): nie schmaler als sein Inhalt (`datum-zeilenfeld`, bausteine.css). W82 Web-5h-N2b: Tippfeld mit Kalender-Knopf,
 *  das Monatsblatt steht als eigene volle Zeile unter Feld und Papierkorb, nie in der schmalen Spalte. */
export function DatumFeld(props: Omit<FeldProps, "label" | "hinweis" | "type"> & { "aria-label": string }) {
  const { className = "", "aria-describedby": eigen, id: eigeneId, ...rest } = props;
  const id = useId();
  return (
    <Datumsfeld id={eigeneId ?? id} className={className} beschrieben={eigen} props={{ ...rest, type: "date" }} zeile />
  );
}

/** Mehrzeiliges Feld in einer Gruppe (Briefe, Nachricht beim Versand); waechst mit dem Text. W73e (S5, D-380 HB11):
 *  `aktion` steht mit der Beschriftung in einer Kopfzeile, die bei Enge umbricht; ohne sie ist das DOM wie bisher. */
export function Textfeld(props: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hinweis?: ReactNode; aktion?: ReactNode; ref?: Ref<HTMLTextAreaElement> }) {
  const { label, hinweis, aktion, className = "", "aria-describedby": eigen, ...rest } = props;
  const id = useId();
  const hinweisId = useId();
  const beschriftung = (
    <label htmlFor={id} className="t-caption-1 t-caption-1-medium text-pg-text-2">
      {label}
    </label>
  );
  return (
    <li className="zeile zeile-feld relative flex flex-col justify-center gap-2 px-4 pt-1 pb-2">
      {aktion ? (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          {beschriftung}
          {aktion}
        </div>
      ) : (
        beschriftung
      )}
      <textarea
        id={id}
        className={`${FELD} min-h-(--feld-h) resize-none py-3 [field-sizing:content] ${className}`}
        aria-describedby={beschreibung(eigen, hinweis ? hinweisId : undefined)}
        {...rest}
      />
      {hinweis && (
        <span id={hinweisId} className="t-footnote text-pg-text-2">
          {hinweis}
        </span>
      )}
    </li>
  );
}
